import { LitElement, css, html, nothing } from "lit";
import type { PropertyValues, TemplateResult } from "lit";
import type { Article } from "@newsfeed/shared";
import type { ApiClient } from "../api.js";
import { ApiError } from "../api.js";
import { formatDate } from "../format.js";
import { sharedStyles } from "../styles.js";

/**
 * Kept articles: list, per-item un-keep, and batch removal guarded by a single
 * confirmation. Removed kept articles are permanently deleted.
 */
export class NfKeptView extends LitElement {
  static override properties = {
    api: { attribute: false },
    articles: { state: true },
    selected: { state: true },
    loading: { state: true },
    error: { state: true },
    pending: { state: true },
  };

  api!: ApiClient;
  articles: Article[] = [];
  selected: Set<string> = new Set();
  loading = false;
  error = "";
  /** Article ids with an in-flight un-keep. */
  pending: Set<string> = new Set();

  static override styles = [
    sharedStyles,
    css`
      header {
        display: flex;
        align-items: center;
        gap: var(--nf-space-12);
        margin-bottom: var(--nf-space-8);
      }
      h2 {
        flex: 1;
        font-size: var(--nf-font-18);
      }
      .item {
        display: flex;
        gap: var(--nf-space-12);
        align-items: flex-start;
        padding: var(--nf-space-12);
        border-bottom: 1px solid var(--nf-border);
      }
      .item:last-child {
        border-bottom: none;
      }
      .main {
        flex: 1;
        min-width: 0;
      }
      .title {
        color: var(--nf-text);
        text-decoration: none;
        font-weight: 600;
      }
      .title:hover {
        color: var(--nf-accent);
      }
      .meta {
        font-size: var(--nf-font-13);
        margin-top: var(--nf-space-2);
      }
    `,
  ];

  override updated(changed: PropertyValues<this>): void {
    if (changed.has("api")) void this.load();
  }

  private async load(showLoading = true): Promise<void> {
    if (!this.api) return;
    if (showLoading) this.loading = true;
    this.error = "";
    try {
      this.articles = await this.api.keptArticles();
      this.selected = new Set();
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    } finally {
      if (showLoading) this.loading = false;
    }
  }

  private toggleSelect(id: string, checked: boolean): void {
    const next = new Set(this.selected);
    if (checked) next.add(id);
    else next.delete(id);
    this.selected = next;
  }

  private async unkeep(id: string): Promise<void> {
    const next = new Set(this.pending);
    next.add(id);
    this.pending = next;
    try {
      await this.api.setKept(id, false);
      await this.load(false);
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    } finally {
      const done = new Set(this.pending);
      done.delete(id);
      this.pending = done;
    }
  }

  private async removeSelected(): Promise<void> {
    const ids = [...this.selected];
    if (ids.length === 0) return;
    if (
      !window.confirm(
        `Permanently remove ${ids.length} kept article(s)? This cannot be undone.`,
      )
    ) {
      return;
    }
    try {
      await this.api.removeArticles(ids);
      await this.load();
    } catch (err) {
      this.error = err instanceof ApiError ? err.message : String(err);
    }
  }

  override render(): TemplateResult {
    return html`
      <header>
        <h2>Kept (${this.articles.length})</h2>
        <button
          class="danger"
          ?disabled=${this.selected.size === 0}
          @click=${() => void this.removeSelected()}
        >
          Remove selected (${this.selected.size})
        </button>
      </header>
      ${this.error ? html`<div class="card error">${this.error}</div>` : nothing}
      ${
        this.loading
          ? html`<div class="empty">Loading…</div>`
          : this.articles.length === 0
            ? html`<div class="empty">No kept articles.</div>`
            : html`<div class="card" style="padding:0">
                ${this.articles.map(
                  (a) =>
                    html`<div class="item">
                      <input
                        type="checkbox"
                        .checked=${this.selected.has(a.id)}
                        @change=${(e: Event) => {
                          if (e.target instanceof HTMLInputElement)
                            this.toggleSelect(a.id, e.target.checked);
                        }}
                      />
                      <div class="main">
                        <a
                          class="title"
                          href=${a.link}
                          target="_blank"
                          rel="noopener noreferrer"
                          @click=${() => void this.api.openArticle(a.id)}
                          >${a.title}</a
                        >
                        <div class="meta muted">
                          ${a.feedTitle} · ${formatDate(a.effectiveDate)}
                        </div>
                      </div>
                      <button
                        ?disabled=${this.pending.has(a.id)}
                        @click=${() => void this.unkeep(a.id)}
                      >
                        ${this.pending.has(a.id) ? html`<span class="spinner"></span>` : nothing}
                        Un-keep
                      </button>
                    </div>`,
                )}
              </div>`
      }
    `;
  }
}

customElements.define("nf-kept-view", NfKeptView);

declare global {
  interface HTMLElementTagNameMap {
    "nf-kept-view": NfKeptView;
  }
}
